import { useSyncExternalStore } from 'react';
import type { RouterHistory } from '@tanstack/react-router';
import { pendingNavigationGuard } from './navigationGuard.js';
import { requestWorkspaceNavigation } from './workspaceNavigation.js';

/**
 * Where this browser tab is in its history, for the top bar's Back/Forward
 * arrows, the in-page "Back to …" links and scroll restoration (dashboard
 * Back/Forward).
 *
 * The browser does not say whether Forward is possible, and TanStack only
 * says whether Back is (`__TSR_index !== 0`). So this module keeps its own
 * trail: for the entry at `__TSR_index` i it records `{ key: __TSR_key,
 * pathname, scrollId }`, per browser tab in sessionStorage. Every push and
 * replace gives an entry a NEW random key, so a key match proves the entry is
 * one we saw; a mismatch means a history we never saw (a new document
 * starting at index 0, cleared storage, a different document's entries) and
 * everything before that entry is treated as unknown.
 *
 * `scrollId` names the entry for scroll restoration (router.tsx
 * getScrollRestorationKey). It is made when the entry is made (its first
 * key) and kept when a filter, sort or typed-text replace rewrites the entry
 * on the same page, so those replaces no longer throw the entry's saved
 * scroll position away.
 *
 * The trail holds random entry keys and pathnames only — no search params,
 * no history state, nothing a person typed. Only signed-in dashboard pages
 * are recorded by pathname; every other page (public review, invite and
 * share links, whose path carries a token) is recorded as `/~`.
 *
 * It is a module-level singleton started once after createRouter
 * (router.tsx), never from a component effect: main.tsx renders under
 * StrictMode, which runs effects twice.
 */

// ---- Pure model (historyPosition.test.ts) ------------------------------------

export interface HistoryEntryRecord {
  readonly key: string;
  readonly pathname: string;
  /** Stable name of this entry for scroll restoration (kept across same-page replaces). */
  readonly scrollId: string;
}

/**
 * `entries[j]` is the entry at absolute history index `base + j`; `null` is an
 * entry we cannot vouch for. `index` is the current absolute index, or null
 * when it is unknown (a missing or non-integer `__TSR_index`).
 */
export interface HistoryTrail {
  readonly base: number;
  readonly entries: ReadonlyArray<HistoryEntryRecord | null>;
  readonly index: number | null;
}

export type HistoryStep = 'START' | 'PUSH' | 'REPLACE' | 'BACK' | 'FORWARD' | 'GO';

/** One observation: the location after a history step (START = boot, reload, bfcache restore). */
export interface HistoryObservation {
  readonly step: HistoryStep;
  /** The location's `__TSR_index` (validated here). */
  readonly index: unknown;
  /** The location's `__TSR_key` (or `key`). */
  readonly key: unknown;
  /** Already redacted (`trailPathname`). */
  readonly pathname: string;
  /**
   * PUSH only: an earlier push in the same tick never became a browser entry
   * (TanStack folds pushes made before its microtask flush into one). The
   * entry before this one is then not real, so it is recorded as unknown.
   */
  readonly coalesced?: boolean;
}

export interface HistoryPosition {
  readonly canGoBack: boolean;
  readonly canGoForward: boolean;
}

/** At most this many entries are kept; older ones fall off the front. */
export const HISTORY_TRAIL_CAP = 500;

/** What the trail records instead of the pathname of a page outside the signed-in dashboard. */
export const REDACTED_PATHNAME = '/~';

export const EMPTY_HISTORY_TRAIL: HistoryTrail = { base: 0, entries: [], index: null };

function validIndex(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function validKey(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 && value.length <= 64 ? value : null;
}

/**
 * The pathname the trail may hold: the real one only for a signed-in
 * dashboard page; `/~` for everything else (token links, sign-in, buyer and
 * seller portals, unknown paths).
 */
export function trailPathname(pathname: string, isDashboardPathname: (pathname: string) => boolean): string {
  let dashboard = false;
  try {
    dashboard = pathname.startsWith('/') && pathname.length <= 2048 && isDashboardPathname(pathname);
  } catch {
    dashboard = false;
  }
  return dashboard ? pathname : REDACTED_PATHNAME;
}

/** The part of `router.getMatchedRoutes(pathname)` the matcher needs. */
export interface MatchedRoutesResult {
  readonly matchedRoutes: ReadonlyArray<{ readonly id: string }>;
  readonly foundRoute: { readonly id: string } | undefined;
  readonly routeParams: Readonly<Record<string, string>>;
}

/**
 * `isDashboardPathname` for the tracker: true when the pathname matches a
 * route under the signed-in app layout (`layoutRouteId`), using the router's
 * own matching, so it follows the route tree as routes are added. A partial
 * match of an unknown path (TanStack's fuzzy match puts the unmatched rest in
 * `'**'`) and the layout itself do not count.
 */
export function dashboardPathnameMatcher(
  getMatchedRoutes: (pathname: string) => MatchedRoutesResult,
  layoutRouteId: string,
): (pathname: string) => boolean {
  return (pathname) => {
    const { matchedRoutes, foundRoute, routeParams } = getMatchedRoutes(pathname);
    if (!foundRoute || foundRoute.id === layoutRouteId) return false;
    if (Object.prototype.hasOwnProperty.call(routeParams, '**')) return false;
    return matchedRoutes.some((route) => route.id === layoutRouteId);
  };
}

function withCap(base: number, entries: Array<HistoryEntryRecord | null>, index: number, cap: number): HistoryTrail {
  const overflow = entries.length - cap;
  if (overflow > 0) return { base: base + overflow, entries: entries.slice(overflow), index };
  return { base, entries, index };
}

/** Fill the gaps of a sparse array with null (so it serialises and compares cleanly). */
function dense(entries: Array<HistoryEntryRecord | null>): Array<HistoryEntryRecord | null> {
  return Array.from(entries, (entry) => entry ?? null);
}

/**
 * The trail after one history step. Rules (map-shell "[Q2 forward detection]"):
 * - START / BACK / FORWARD / GO: if the entry at i is the one we recorded
 *   (same key), only the index moves and forward entries are kept. Otherwise
 *   it is unknown: forward entries are dropped and the entries before it are
 *   marked unknown (null), so `previousEntryPathname` never vouches for an
 *   entry from a history we did not see.
 * - PUSH: forward entries are dropped (the browser drops them too) and the new
 *   entry is recorded at i, with a new scroll id.
 * - REPLACE: the entry at i is re-recorded; forward entries are kept
 *   (replaceState keeps them). It keeps its scroll id when it stays on the
 *   same dashboard page (a filter, sort or text write); a replace onto
 *   another page (a redirect) starts a new one.
 * - A missing or non-integer index (or key) makes the position unknown: both
 *   arrows off, the recorded entries kept for when a valid index returns.
 */
export function advanceHistoryTrail(
  trail: HistoryTrail,
  observation: HistoryObservation,
  cap: number = HISTORY_TRAIL_CAP,
): HistoryTrail {
  const index = validIndex(observation.index);
  const key = validKey(observation.key);
  if (index === null || key === null) return { ...trail, index: null };
  const fresh: HistoryEntryRecord = { key, pathname: observation.pathname, scrollId: key };

  // Before the oldest entry we still hold: nothing earlier or later can be vouched for.
  if (index < trail.base) return { base: index, entries: [fresh], index };

  const j = index - trail.base;
  switch (observation.step) {
    case 'PUSH': {
      const entries = dense(trail.entries.slice(0, j));
      if (observation.coalesced && j > 0) entries[j - 1] = null;
      entries[j] = fresh;
      return withCap(trail.base, dense(entries), index, cap);
    }
    case 'REPLACE': {
      const entries = dense([...trail.entries]);
      const previous = trail.entries[j] ?? null;
      const samePage =
        previous !== null &&
        trail.index === index &&
        previous.pathname === observation.pathname &&
        observation.pathname !== REDACTED_PATHNAME;
      entries[j] = samePage ? { ...fresh, scrollId: previous.scrollId } : fresh;
      return withCap(trail.base, dense(entries), index, cap);
    }
    case 'START':
    case 'BACK':
    case 'FORWARD':
    case 'GO': {
      if (trail.entries[j]?.key === key) return { ...trail, index };
      const entries: Array<HistoryEntryRecord | null> = Array.from({ length: j }, () => null);
      entries[j] = fresh;
      return withCap(trail.base, entries, index, cap);
    }
  }
}

export function historyTrailPosition(trail: HistoryTrail): HistoryPosition {
  if (trail.index === null) return { canGoBack: false, canGoForward: false };
  const j = trail.index - trail.base;
  return { canGoBack: trail.index > 0, canGoForward: trail.entries.length > j + 1 };
}

/** The pathname of the entry right before the current one, when we recorded it; else null. */
export function historyTrailPreviousPathname(trail: HistoryTrail): string | null {
  if (trail.index === null || trail.index === 0) return null;
  return trail.entries[trail.index - 1 - trail.base]?.pathname ?? null;
}

function samePathname(a: string, b: string): boolean {
  const trim = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p);
  return trim(a) === trim(b);
}

/**
 * How many entries back the page `target` is, walking back over the entries
 * of the CURRENT page (tabs and items opened inside it): the first entry with
 * another pathname must be `target`, and every entry walked over must be one
 * we recorded. Null when that cannot be vouched for (the caller then
 * navigates to its fallback instead).
 */
export function historyTrailStepsBackTo(trail: HistoryTrail, target: string): number | null {
  if (trail.index === null) return null;
  const j = trail.index - trail.base;
  const current = trail.entries[j];
  if (!current || current.pathname === REDACTED_PATHNAME) return null;
  for (let k = j - 1; k >= 0; k -= 1) {
    const entry = trail.entries[k];
    if (!entry) return null;
    if (samePathname(entry.pathname, current.pathname)) continue;
    return entry.pathname !== REDACTED_PATHNAME && samePathname(entry.pathname, target) ? j - k : null;
  }
  return null;
}

/** The scroll id of the recorded entry with this key, or null. */
export function historyTrailScrollId(trail: HistoryTrail, key: string): string | null {
  for (let j = trail.entries.length - 1; j >= 0; j -= 1) {
    const entry = trail.entries[j];
    if (entry?.key === key) return entry.scrollId;
  }
  return null;
}

/** The parts of a router location scroll restoration is keyed on. */
export interface ScrollKeyLocation {
  readonly state: { readonly __TSR_key?: unknown; readonly __TSR_index?: unknown; readonly key?: unknown };
  readonly href: string;
}

/**
 * The scroll restoration key for a location:
 * 1. a key we saw (also one a replace has since retired: TanStack snapshots
 *    the scroll of the location it is leaving under that location's OLD key),
 * 2. a key the trail holds,
 * 3. a key not observed yet at the current index on the same page — a
 *    replace the tracker has not seen yet — gets the current entry's id,
 * 4. otherwise TanStack's own default (`__TSR_key`, else the href).
 */
export function scrollRestorationKeyFor(
  trail: HistoryTrail,
  known: ReadonlyMap<string, string>,
  location: ScrollKeyLocation,
  pathname: string,
): string {
  const rawKey = location.state.__TSR_key ?? location.state.key;
  const key = validKey(rawKey);
  if (key !== null) {
    const seen = known.get(key) ?? historyTrailScrollId(trail, key);
    if (seen !== null) return seen;
    const index = validIndex(location.state.__TSR_index);
    if (index !== null && index === trail.index) {
      const current = trail.entries[index - trail.base];
      if (current && current.pathname === pathname && pathname !== REDACTED_PATHNAME) return current.scrollId;
    }
  }
  const tsrKey = location.state.__TSR_key;
  return typeof tsrKey === 'string' && tsrKey ? tsrKey : location.href;
}

const STORAGE_VERSION = 1;

/**
 * The stored form: entries only (the index always comes from the live
 * location). An entry is `[key, pathname]`, or `[key, pathname, scrollId]`
 * once a replace has given it a key other than its scroll id.
 */
export function serializeHistoryTrail(trail: HistoryTrail): string {
  return JSON.stringify({
    v: STORAGE_VERSION,
    base: trail.base,
    entries: trail.entries.map((entry) => {
      if (!entry) return null;
      return entry.scrollId === entry.key ? [entry.key, entry.pathname] : [entry.key, entry.pathname, entry.scrollId];
    }),
  });
}

/** Read a stored trail; anything unreadable is an empty trail (never throws). */
export function parseStoredHistoryTrail(raw: string | null | undefined): HistoryTrail {
  if (!raw) return EMPTY_HISTORY_TRAIL;
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return EMPTY_HISTORY_TRAIL;
  }
  if (typeof value !== 'object' || value === null) return EMPTY_HISTORY_TRAIL;
  const { v, base, entries } = value as { v?: unknown; base?: unknown; entries?: unknown };
  if (v !== STORAGE_VERSION || validIndex(base) === null || !Array.isArray(entries)) return EMPTY_HISTORY_TRAIL;
  if (entries.length > HISTORY_TRAIL_CAP) return EMPTY_HISTORY_TRAIL;
  const out: Array<HistoryEntryRecord | null> = [];
  for (const entry of entries as unknown[]) {
    const key = Array.isArray(entry) ? validKey(entry[0]) : null;
    const scrollId = Array.isArray(entry) && entry.length === 3 ? validKey(entry[2]) : key;
    if (
      Array.isArray(entry) &&
      (entry.length === 2 || entry.length === 3) &&
      key !== null &&
      scrollId !== null &&
      typeof entry[1] === 'string' &&
      entry[1].startsWith('/') &&
      entry[1].length <= 2048
    ) {
      out.push({ key, pathname: entry[1], scrollId });
    } else {
      out.push(null);
    }
  }
  return { base: base as number, entries: out, index: null };
}

// ---- The singleton -------------------------------------------------------------

export const HISTORY_TRAIL_STORAGE_KEY = 'yrp:history-entries:v1';

type HistorySubscriberArgs = Parameters<Parameters<RouterHistory['subscribe']>[0]>[0];

/** What the tracker needs from the router (router.tsx passes these). */
export interface HistoryPositionRouter {
  history: RouterHistory;
  /** In-app navigation to an href (path plus optional search), used by goBackTo's fallback. */
  navigateHref: (href: string) => void;
  /**
   * Whether a pathname is a signed-in dashboard page (router.tsx: it matches
   * a route under the app layout). Anything else is recorded as `/~`.
   * Without it nothing is recorded by pathname.
   */
  isDashboardPathname?: (pathname: string) => boolean;
}

let trail: HistoryTrail = EMPTY_HISTORY_TRAIL;
let position: HistoryPosition = { canGoBack: false, canGoForward: false };
let activeRouter: HistoryPositionRouter | null = null;
const listeners = new Set<() => void>();

/** Every entry key seen in this document → its entry's scroll id (memory only, capped). */
const scrollIdByKey = new Map<string, string>();
const SCROLL_KEY_MEMORY = 1000;
/** A PUSH was seen and TanStack has not flushed it to the browser yet (same tick). */
let pushInThisTick = false;

function rememberScrollKey(record: HistoryEntryRecord | null | undefined): void {
  if (!record) return;
  scrollIdByKey.delete(record.key);
  scrollIdByKey.set(record.key, record.scrollId);
  if (scrollIdByKey.size > SCROLL_KEY_MEMORY) {
    const oldest = scrollIdByKey.keys().next();
    if (!oldest.done) scrollIdByKey.delete(oldest.value);
  }
}

function readStorage(): string | null {
  try {
    return window.sessionStorage.getItem(HISTORY_TRAIL_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStorage(value: HistoryTrail): void {
  try {
    window.sessionStorage.setItem(HISTORY_TRAIL_STORAGE_KEY, serializeHistoryTrail(value));
  } catch {
    // Storage blocked or full: the arrows still work for this document.
  }
}

function recordedPathname(pathname: string): string {
  const isDashboard = activeRouter?.isDashboardPathname;
  return isDashboard ? trailPathname(pathname, isDashboard) : REDACTED_PATHNAME;
}

function observe(step: HistoryStep, location: HistorySubscriberArgs['location']): void {
  const state = location.state as { __TSR_index?: unknown; __TSR_key?: unknown; key?: unknown } | undefined;
  const coalesced = step === 'PUSH' && pushInThisTick;
  if (step === 'PUSH' && !pushInThisTick) {
    pushInThisTick = true;
    // Runs after TanStack's own flush microtask (queued before this notify).
    queueMicrotask(() => {
      pushInThisTick = false;
    });
  }
  trail = advanceHistoryTrail(trail, {
    step,
    index: state?.__TSR_index,
    key: state?.__TSR_key ?? state?.key,
    pathname: recordedPathname(location.pathname),
    coalesced,
  });
  if (trail.index !== null) rememberScrollKey(trail.entries[trail.index - trail.base]);
  writeStorage(trail);
  const next = historyTrailPosition(trail);
  if (next.canGoBack !== position.canGoBack || next.canGoForward !== position.canGoForward) {
    position = next;
    listeners.forEach((listener) => listener());
  }
}

/**
 * Start tracking (once; later calls return the same stop function's effect).
 * Call right after createRouter, outside React.
 */
export function startHistoryPositionTracking(router: HistoryPositionRouter): () => void {
  if (activeRouter) return () => {};
  if (typeof window === 'undefined') return () => {};
  activeRouter = router;
  trail = parseStoredHistoryTrail(readStorage());
  observe('START', router.history.location);
  const unsubscribe = router.history.subscribe(({ location, action }) => observe(action.type, location));
  // A page restored from the back-forward cache keeps its in-memory trail but
  // another document in this tab may have rewritten storage meanwhile.
  const onPageShow = (event: PageTransitionEvent) => {
    if (!event.persisted) return;
    trail = parseStoredHistoryTrail(readStorage());
    observe('START', router.history.location);
  };
  window.addEventListener('pageshow', onPageShow);
  return () => {
    unsubscribe();
    window.removeEventListener('pageshow', onPageShow);
    activeRouter = null;
  };
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The arrows' current position (both false when the position is unknown). */
export function getHistoryPosition(): HistoryPosition {
  return position;
}

/** Whether the top bar's Back / Forward arrows can move (both false when the position is unknown). */
export function useHistoryPosition(): HistoryPosition {
  return useSyncExternalStore(subscribe, getHistoryPosition, getHistoryPosition);
}

/** The pathname of the previous entry in this tab, when this document recorded it; else null. */
export function previousEntryPathname(): string | null {
  return historyTrailPreviousPathname(trail);
}

/**
 * router.tsx `getScrollRestorationKey`: one key per history ENTRY (kept
 * across same-page replaces), so a filter, sort or typed-text replace does
 * not lose the entry's saved scroll position. Falls back to TanStack's
 * default (`__TSR_key`, else the href) for entries this document never saw.
 */
export function historyScrollRestorationKey(location: ScrollKeyLocation & { readonly pathname?: string }): string {
  const pathname = typeof location.pathname === 'string' ? recordedPathname(location.pathname) : REDACTED_PATHNAME;
  return scrollRestorationKeyFor(trail, scrollIdByKey, location, pathname);
}

/** What an in-page "Back to …" link does: step back `steps` entries, or open its fallback (a push). */
export type GoBackToPlan = { readonly kind: 'go'; readonly steps: number } | { readonly kind: 'fallback' };

export interface GoBackToChecks {
  /** pendingNavigationGuard: an unsaved-changes message, or null. */
  readonly pendingGuard: () => string | null;
  /**
   * requestWorkspaceNavigation({ kind: 'back' }): true when open work (an
   * email being sent, an upload) refused the move.
   */
  readonly openWorkRefuses: () => boolean;
}

/**
 * Whether the "Back to …" link may step back several entries at once.
 *
 * One entry back is the browser's own Back (`history.go(-1)`): a page's
 * blocker that cancels it is rolled back exactly by TanStack (`go(1)`).
 * Several entries back (`go(-n)`, the list behind tabs or items opened on
 * this page) is not: @tanstack/history rolls a cancelled pop back with a
 * hard-coded `go(1)`, which leaves the browser n - 1 entries short of the
 * screen and the address bar, and the next click overshoots. So when
 * anything could cancel the move — a page with unsaved changes (its guard),
 * or open work that refuses — the link opens its fallback instead: a push
 * is asked about BEFORE history moves, so a Cancel leaves everything as it
 * was. Open work is asked only for a multi-entry step (asking has an effect:
 * the composer saves its snapshot or shows why it refused).
 */
export function planGoBackTo(steps: number | null, checks: GoBackToChecks): GoBackToPlan {
  if (steps === null || !Number.isSafeInteger(steps) || steps <= 0) return { kind: 'fallback' };
  if (steps === 1) return { kind: 'go', steps };
  if (checks.pendingGuard() !== null) return { kind: 'fallback' };
  if (checks.openWorkRefuses()) return { kind: 'fallback' };
  return { kind: 'go', steps };
}

/**
 * An in-page "Back to …" link. When the page you came from is exactly that
 * page (e.g. the contact list, before any tabs or items you opened on this
 * page), go back to it — that restores it as you left it, with its filters,
 * history-state text and scroll. Otherwise navigate (push) to `fallback` (an
 * href; defaults to `pathname`).
 *
 * The step is the browser's own (`history.go`): the popstate runs every
 * page's blocker as Back does. (TanStack's `history.back()` would also turn
 * off the next "Leave site?" prompt on reload or close, even much later.)
 * A step of more than one entry is taken only when nothing could cancel it
 * (planGoBackTo); otherwise the fallback push asks first.
 *
 *   goBackTo('/transactions')
 *   goBackTo({ pathname: '/crm', fallback: '/crm' })
 */
export function goBackTo(target: string | { pathname: string; fallback?: string }): void {
  const pathname = typeof target === 'string' ? target : target.pathname;
  const fallback = typeof target === 'string' ? target : (target.fallback ?? target.pathname);
  const router = activeRouter;
  if (!router) return;
  const plan = planGoBackTo(historyTrailStepsBackTo(trail, pathname), {
    pendingGuard: pendingNavigationGuard,
    openWorkRefuses: () => requestWorkspaceNavigation({ kind: 'back', href: null }),
  });
  if (plan.kind === 'go') {
    window.history.go(-plan.steps);
    return;
  }
  router.navigateHref(fallback);
}
